import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Calendar, BarChart3, Users, Eye, X, Loader2, MonitorPlay, MapPin } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    PieChart, Pie, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE = [
    '#1B365D', '#C9A962', '#10B981', '#0EA5E9', '#8B5CF6', '#EF4444',
    '#F59E0B', '#14B8A6', '#EC4899', '#6366F1', '#84CC16', '#64748B',
];

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const j = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${j}`;
};

export default function ProspectsStatsBloc() {
    const [datePreset, setDatePreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [groupBy, setGroupBy] = useState('statut'); // 'statut' | 'source'
    const [chartType, setChartType] = useState('bar'); // 'bar' | 'pie'
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const applyDatePreset = (p) => {
        setDatePreset(p);
        const today = new Date(); const iso = toISO(today);
        if (p === 'all') { setDateFrom(''); setDateTo(''); return; }
        if (p === 'today') { setDateFrom(iso); setDateTo(iso); return; }
        if (p === '7d') { const d = new Date(); d.setDate(d.getDate() - 6); setDateFrom(toISO(d)); setDateTo(iso); return; }
        if (p === '30d') { const d = new Date(); d.setDate(d.getDate() - 29); setDateFrom(toISO(d)); setDateTo(iso); return; }
    };

    const clearDates = () => { setDatePreset('all'); setDateFrom(''); setDateTo(''); };

    useEffect(() => {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/prospects`, { params, withCredentials: true })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [dateFrom, dateTo]);

    const chartData = useMemo(() => {
        if (!data) return [];
        const rows = groupBy === 'statut' ? data.by_statut : data.by_source;
        return (rows || []).map((r, i) => ({ name: r.label, value: r.count, key: r.key, fill: PALETTE[i % PALETTE.length] }));
    }, [data, groupBy]);

    const total = data?.total || 0;
    const vt = data?.visit_types || {};

    return (
        <Card className="border-t-4 border-t-[#1B365D] shadow-sm" data-testid="stats-bloc-prospects">
            <CardContent className="p-5 sm:p-6 space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <div className="h-10 w-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                            <BarChart3 className="h-5 w-5 text-[#1B365D]" />
                        </div>
                        <div>
                            <h3 className="font-bold text-[#1B365D] text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Prospects — Répartition
                            </h3>
                            <p className="text-xs text-gray-500">Par statut, source et type de visite</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-[#1B365D] bg-[#1B365D]/5 rounded-lg px-3 py-1.5">
                        <Users className="w-4 h-4" />
                        <span className="text-xl font-bold" data-testid="stats-total">{total}</span>
                        <span className="text-xs text-gray-500">prospects</span>
                    </div>
                </div>

                {/* Filters: period + groupBy + chartType */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="stats-filters">
                    <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <div className="flex flex-wrap gap-0.5 bg-gray-100 p-0.5 rounded-md">
                        {[
                            { v: 'all', label: 'Tout' },
                            { v: 'today', label: "Aujourd'hui" },
                            { v: '7d', label: '7 jours' },
                            { v: '30d', label: '30 jours' },
                            { v: 'custom', label: 'Personnalisé' },
                        ].map(p => (
                            <button key={p.v} onClick={() => applyDatePreset(p.v)}
                                className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${datePreset === p.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                                data-testid={`stats-preset-${p.v}`}
                            >{p.label}</button>
                        ))}
                    </div>
                    {datePreset === 'custom' && (
                        <div className="flex items-center gap-1.5">
                            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="stats-date-from" />
                            <span className="text-gray-400 text-[10px]">→</span>
                            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="stats-date-to" />
                        </div>
                    )}
                    {(dateFrom || dateTo) && (
                        <button onClick={clearDates} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-testid="stats-clear-dates">
                            <X className="w-3 h-3" /> Effacer
                        </button>
                    )}

                    <span className="mx-2 h-5 w-px bg-gray-200 hidden sm:inline-block" />

                    <div className="flex flex-wrap gap-0.5 bg-gray-100 p-0.5 rounded-md">
                        {['statut', 'source'].map(g => (
                            <button key={g} onClick={() => setGroupBy(g)}
                                className={`px-2.5 py-1 rounded text-[11px] font-medium capitalize transition-colors ${groupBy === g ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                                data-testid={`stats-group-${g}`}
                            >Par {g}</button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-0.5 bg-gray-100 p-0.5 rounded-md">
                        {[
                            { v: 'bar', label: 'Barres' },
                            { v: 'pie', label: 'Camembert' },
                        ].map(c => (
                            <button key={c.v} onClick={() => setChartType(c.v)}
                                className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${chartType === c.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                                data-testid={`stats-chart-${c.v}`}
                            >{c.label}</button>
                        ))}
                    </div>
                </div>

                {/* Chart */}
                <div className="bg-gray-50/50 border border-gray-100 rounded-lg p-3" style={{ height: 320 }} data-testid="stats-chart-container">
                    {loading ? (
                        <div className="h-full flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    ) : chartData.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm">
                            <BarChart3 className="w-10 h-10 mb-2 text-gray-300" />
                            Aucune donnée sur la période sélectionnée
                        </div>
                    ) : chartType === 'bar' ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                                <XAxis
                                    dataKey="name"
                                    tick={{ fontSize: 11, fill: '#475569' }}
                                    interval={0}
                                    angle={-25}
                                    textAnchor="end"
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                                <Tooltip
                                    cursor={{ fill: 'rgba(27,54,93,0.05)' }}
                                    contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                                    formatter={(v) => [`${v} (${total ? Math.round((v / total) * 100) : 0}%)`, 'Prospects']}
                                />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                    {chartData.map((entry, i) => (
                                        <Cell key={`cell-${i}`} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    outerRadius={100}
                                    label={(e) => `${total ? Math.round((e.value / total) * 100) : 0}%`}
                                    labelLine={false}
                                >
                                    {chartData.map((entry, i) => <Cell key={`pie-${i}`} fill={entry.fill} />)}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                                    formatter={(v, n) => [`${v} (${total ? Math.round((v / total) * 100) : 0}%)`, n]}
                                />
                                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Visit types KPI */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="stats-visit-types">
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Visite présentielle</div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-bold text-emerald-700" data-testid="stats-pct-presentiel">{vt.pct_presentiel ?? 0}%</span>
                                <span className="text-xs text-gray-500">{vt.presentiel ?? 0} prospect{(vt.presentiel ?? 0) > 1 ? 's' : ''}</span>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border border-sky-100 bg-sky-50/50 p-3 flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                            <MonitorPlay className="w-4 h-4 text-sky-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Visite virtuelle</div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-bold text-sky-700" data-testid="stats-pct-virtuel">{vt.pct_virtuel ?? 0}%</span>
                                <span className="text-xs text-gray-500">{vt.virtuel ?? 0} prospect{(vt.virtuel ?? 0) > 1 ? 's' : ''}</span>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border border-[#1B365D]/20 bg-[#1B365D]/5 p-3 flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                            <Eye className="w-4 h-4 text-[#1B365D]" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Total prospects</div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-bold text-[#1B365D]">{total}</span>
                                <span className="text-xs text-gray-500">sur la période</span>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
